/**
 * Camada 1 · topbar.
 *
 * A DIREITA, NESTA ORDEM, E O MESMO EM TODO PRODUTO: sino, ajuda, idioma e tema. A conta
 * desceu para o pe do menu. O canto direito superior e o endereco fixo desses controles na
 * familia de sistemas.
 *
 * O SAIR SAIU DAQUI em 15/09/2026, pedido do fundador: "tirar o sair do cabecalho do chassi".
 * Entre 12/09 e 15/09 o Medicina e o admin tinham o icone por ultimo, a direita, pela opcao
 * `sairNoCabecalho`, que foi removida do contrato. O Sair mora so no painel da conta.
 *
 * O MENU DO USUARIO TRAZ O SAIR NO TOPO, ACIMA das opcoes do usuario, e nunca dentro de
 * uma tela de perfil. Sair nao e ajuste de preferencia, e saida, e quem quer sair tem
 * pressa. Dois defeitos reais compraram essa regra: num sistema o Sair morava so na tela
 * de Perfil, que no celular ficava desenhada fora da largura visivel, entao nao havia
 * como sair pelo telefone; em outro, ele navegava para uma rota de login que aquele app
 * nunca teve, e a pessoa caia numa pagina de erro.
 */

import { t } from '@complexity/i18n';
// OS ICONES SAO DESENHADOS, e nunca caractere. Emoji e simbolo Unicode mudam de forma
// por sistema operacional e por fonte instalada, entao o sino saia amarelo e gordo no
// Mac e chapado no Windows, dentro de uma barra que e a mesma em todos os produtos.
import { IconeAjuda, IconeMenu, IconeSino } from '@complexity/ui';
import { ControlesIdiomaTema } from '@complexity/ui';

export interface OpcaoUsuario {
  readonly label: string;
  readonly aoClicar: () => void;
}

export interface TopbarProps {
  readonly titulo: string;
  /** Numero de avisos nao lidos. Zero esconde o contador, e nao desenha "0". */
  readonly naoLidos: number;
  readonly aoAbrirAvisos: () => void;
  /**
   * O nome falado do sino, já pronto, quando o produto declara o próprio: o chassi monta o
   * singular e o plural, e esta peça só o fala. Ausente, o sino diz "Avisos" e "Avisos: N",
   * como sempre disse, e é o caso do VOE.
   */
  readonly rotuloDoSino?: string;
  readonly aoAbrirAjuda: () => void;
  /** O icone de ajuda aparece? Padrao: sim. Vem de `ajuda.noCabecalho` do manifesto. */
  readonly mostrarAjuda?: boolean;
  /** Abre a gaveta do rail. So aparece no celular. */
  readonly aoAbrirMenu: () => void;
}

export function Topbar({
  titulo,
  naoLidos,
  aoAbrirAvisos,
  rotuloDoSino,
  aoAbrirAjuda,
  mostrarAjuda = true,
  aoAbrirMenu,
}: TopbarProps) {
  return (
    // DUAS LINHAS NO CELULAR, UMA DO TABLET PARA CIMA.
    //
    // DEFEITO MEDIDO EM 27/08/2026, E ESTA E A CORRECAO: em 390px o titulo tinha largura
    // ZERO. Os controles da direita somam cerca de 360px dos 366px uteis, e o `flex-1` do
    // titulo cedia ate nada. Ele existia na arvore e o leitor de tela o anunciava, mas
    // ninguem o via, que e o mesmo padrao do contador do sino que ja custou caro.
    //
    // A correcao NAO esconde nada no celular, de proposito: o app precisa funcionar igual
    // em toda maquina, e remover a ajuda ou o sino do telefone seria trocar um defeito por
    // outro. O que muda e o arranjo: `flex-wrap` deixa o titulo descer para a segunda
    // linha, em largura inteira, e do tablet para cima tudo volta a caber numa linha so.
    <header className="sticky top-0 z-topbar flex flex-wrap items-center gap-x-cx-3 gap-y-cx-1 border-b border-borda bg-superficie px-cx-3 py-cx-1">
      <button
        type="button"
        onClick={aoAbrirMenu}
        aria-label={t('Abrir menu')}
        className="order-1 shrink-0 min-h-toque min-w-toque rounded-cx-md text-texto-suave hover:bg-superficie-afundada md:hidden"
      >
        <IconeMenu className="mx-auto h-5 w-5" />
      </button>

      {/* No celular o titulo vai para a SEGUNDA linha, com largura inteira. Do tablet para
          cima ele volta para a primeira, entre o menu e os controles. */}
      <h1 className="order-3 w-full min-w-0 truncate text-cx-sm font-semibold text-texto md:order-2 md:w-auto md:flex-1">
        {titulo}
      </h1>

      {/* Bloco da DIREITA. `ml-auto` o mantem colado na direita mesmo quando o titulo
          desce, e `shrink-0` impede que ele ceda largura e comprima os controles. */}
      <div className="order-2 ml-auto flex shrink-0 items-center gap-cx-2 md:order-3">
        <button
          type="button"
          onClick={aoAbrirAvisos}
          // O numero entra no rotulo falado, porque um ponto colorido nao diz quantos sao.
          // O defeito que comprou isto: o contador estava certo, a API devolvia 2, e a
          // pessoa via so um ponto verde sem numero, porque o numero estava preso a
          // estilos que so valiam de tablet para cima.
          //
          // O NOME DECLARADO PELO PRODUTO VENCE, e o padrão continua sendo "Avisos". Mudou em
          // 11/09/2026, com esta peça congelada liberada pela enquete do fundador: o VOE não
          // declara nome, então o que ele fala continua igual.
          aria-label={rotuloDoSino ?? (naoLidos > 0 ? t('Avisos') + `: ${naoLidos}` : t('Avisos'))}
          className="relative min-h-toque min-w-toque rounded-cx-md text-texto-suave hover:bg-superficie-afundada"
        >
          <IconeSino className="mx-auto h-5 w-5" />
          {naoLidos > 0 && (
            // SEM prefixo de largura de proposito. Um `md:` aqui faria o numero nao
            // existir no celular, que e exatamente o defeito que esta regra evita.
            <span
              aria-hidden="true"
              className="absolute right-0 top-0 flex h-4 min-w-4 items-center justify-center rounded-cx-full bg-erro px-cx-1 text-cx-xs font-semibold text-marca-contraste"
            >
              {naoLidos > 99 ? '99+' : naoLidos}
            </span>
          )}
        </button>

        {/*
          O ÍCONE DE AJUDA SÓ APARECE SE O PRODUTO QUISER, desde 07/10/2026.

          Pedido do fundador sobre o app da cliente: "tirar botao de ajuda da tela da cliente (em
          todos os dispositivos)". Naquele produto a ajuda já estava desligada por todos os outros
          caminhos, e este ícone era o último resto: ele continuava na tela e não levava a lugar
          nenhum. O interruptor mora no manifesto (`ajuda.noCabecalho`), e não aqui, porque é
          decisão de produto, e não de desenho do cabeçalho.

          O PADRÃO CONTINUA SENDO MOSTRAR: produto que não declara nada segue como estava.
        */}
        {mostrarAjuda && (
          <button
            type="button"
            onClick={aoAbrirAjuda}
            aria-label={t('Ajuda')}
            className="min-h-toque min-w-toque rounded-cx-md text-texto-suave hover:bg-superficie-afundada"
          >
            <IconeAjuda className="mx-auto h-5 w-5" />
          </button>
        )}

        <ControlesIdiomaTema />

        {/* A CONTA E AS CONFIGURACOES DESCERAM PARA O RODAPE DO MENU em 30/08/2026, por
            pedido dele: "TB CONFIGURACOES E USUARIO VAO PRA BAIXO NO MENU". O que ficou
            aqui e o que a regra dura do CLAUDE geral prende ao frame superior direito: o
            idioma e o tema, logo acima. Ver o rodape do `Rail`. */}
      </div>
    </header>
  );
}
